import { appelerFonction, supabase } from './supabase'
import { statutPiece } from '../onboarding/pieces'
import { traduireErreurOnboarding } from '../onboarding/erreurs'
import {
  LIBELLE_DOC_LIVREUR,
  LIBELLE_DOC_MARCHAND,
  type CandidatureLivreur,
  type CandidatureMarchand,
  type CompteReversement,
  type DossierLivreur,
  type DossierMarchand,
  type EntretienEnregistre,
  type EvenementJournal,
  type FormationEnregistree,
  type ModeleContrat,
  type PieceDossier,
  type LigneIdentite,
  type ReponseContratRendu,
  type ReponseEnvoiContrat,
  type SignatureContrat,
  type TentativeQuiz,
} from '../onboarding/types'
import type { Recommandation } from '../onboarding/canevas-types'
import { versPayload, type IdentiteSaisie, type SujetContrat } from '../onboarding/identite'

/**
 * Données de l'écran « Candidatures » (migration 0045).
 *
 * Aucune règle métier n'est rejouée ici : transitions de statut, validité des
 * pièces, quiz, signature… sont décidées par la base. Chaque erreur est
 * traduite en phrase AVANT de remonter à l'écran (jamais un code brut).
 */

/** Lève une `Error` à message lisible à partir d'une erreur PostgREST/Storage. */
function lever(error: { message?: string } | null): void {
  if (error) throw new Error(traduireErreurOnboarding(error.message))
}

/** Une mise à jour qui ne touche aucune ligne = droit manquant (RLS) ou ligne absente : on le dit. */
function exigerLigne(data: unknown[] | null, quoi: string): void {
  if (!data || data.length === 0) {
    throw new Error(`${quoi} : aucune ligne modifiée (droit manquant ou dossier introuvable).`)
  }
}

// ── Listes ───────────────────────────────────────────────────────────────

interface LigneCandidatureLivreur {
  driver_id: string
  status: string
  permis_requis: boolean
  motif: string | null
  created_at: string | null
  updated_at: string | null
  driver: {
    vehicle_type: string | null
    vehicle_plate: string | null
    vehicle_model: string | null
    payout_phone: string | null
    profile: { full_name: string | null; phone: string | null; email: string | null } | null
  } | null
}

export async function listerCandidaturesLivreurs(): Promise<CandidatureLivreur[]> {
  const { data, error } = await supabase
    .from('driver_onboarding')
    .select(
      'driver_id,status,permis_requis,motif,created_at,updated_at,' +
        'driver:drivers!driver_id(vehicle_type,vehicle_plate,vehicle_model,payout_phone,profile:profiles!id(full_name,phone,email))',
    )
    .order('updated_at', { ascending: false })
  lever(error)
  return ((data as unknown as LigneCandidatureLivreur[] | null) ?? []).map((o) => ({
    id: o.driver_id,
    nom: o.driver?.profile?.full_name ?? 'Livreur',
    telephone: o.driver?.profile?.phone ?? o.driver?.payout_phone ?? '',
    email: o.driver?.profile?.email ?? '',
    vehicule: [o.driver?.vehicle_type, o.driver?.vehicle_plate].filter(Boolean).join(' '),
    statut: o.status,
    motif: o.motif,
    permisRequis: o.permis_requis,
    majLe: o.updated_at,
    creeLe: o.created_at,
  }))
}

interface LigneCandidatureMarchand {
  id: string
  name: string
  kind: string | null
  quartier: string | null
  phone: string | null
  owner_id: string | null
  onboarding_status: string
  onboarding_motif: string | null
  onboarding_updated_at: string | null
  created_at: string | null
  gerant: { email: string | null; full_name: string | null; phone: string | null } | null
}

export async function listerCandidaturesMarchands(): Promise<CandidatureMarchand[]> {
  const { data, error } = await supabase
    .from('merchants')
    .select(
      'id,name,kind,quartier,phone,owner_id,onboarding_status,onboarding_motif,onboarding_updated_at,created_at,' +
        'gerant:profiles!merchants_owner_id_fkey(email,full_name,phone)',
    )
    .order('created_at', { ascending: false })
  lever(error)
  return ((data as unknown as LigneCandidatureMarchand[] | null) ?? []).map((m) => ({
    id: m.id,
    nom: m.name,
    type: m.kind === 'supermarket' ? 'Supermarché' : 'Restaurant',
    quartier: m.quartier ?? '',
    telephone: m.phone ?? m.gerant?.phone ?? '',
    emailGerant: m.gerant?.email ?? '',
    nomGerant: m.gerant?.full_name ?? '',
    proprietaireId: m.owner_id,
    statut: m.onboarding_status,
    motif: m.onboarding_motif,
    majLe: m.onboarding_updated_at,
    creeLe: m.created_at,
  }))
}

// ── Dossiers ─────────────────────────────────────────────────────────────

interface LignePiece {
  id: string
  doc_type: string
  file_url: string
  is_verified: boolean
  rejected_reason: string | null
  uploaded_at: string | null
  issued_at?: string | null
  expires_at?: string | null
}

function versPiece(p: LignePiece, libelles: Record<string, string>): PieceDossier {
  const motifRejet = p.rejected_reason
  return {
    id: p.id,
    type: p.doc_type,
    libelle: libelles[p.doc_type] ?? p.doc_type,
    chemin: p.file_url,
    valide: p.is_verified,
    motifRejet,
    statut: statutPiece({ valide: p.is_verified, motifRejet }),
    deposeLe: p.uploaded_at,
    emisLe: p.issued_at ?? null,
    expireLe: p.expires_at ?? null,
  }
}

interface LigneSignature {
  id: string
  signer_id: string
  template_id: string
  template_version: number
  kind: 'livreur' | 'commercant'
  merchant_id: string | null
  method: 'app' | 'documenso'
  status: 'envoye' | 'signe' | 'refuse' | 'expire'
  accepted_at: string | null
  created_at: string
  lien_signature: string | null
  destinataire_email: string | null
  exclusivite_debut: string | null
  exclusivite_fin: string | null
}

const COLONNES_SIGNATURE =
  'id,signer_id,template_id,template_version,kind,merchant_id,method,status,accepted_at,created_at,lien_signature,destinataire_email,exclusivite_debut,exclusivite_fin'

const versSignature = (s: LigneSignature): SignatureContrat => ({
  id: s.id,
  signataire: s.signer_id,
  modeleId: s.template_id,
  version: s.template_version,
  kind: s.kind,
  marchandId: s.merchant_id,
  methode: s.method,
  statut: s.status,
  accepteLe: s.accepted_at,
  creeLe: s.created_at,
  lien: s.lien_signature,
  email: s.destinataire_email,
  exclusiviteDebut: s.exclusivite_debut,
  exclusiviteFin: s.exclusivite_fin,
})

interface LigneJournal {
  id: number
  de: string | null
  vers: string
  motif: string | null
  at: string
  auteur: { full_name: string | null } | null
}

async function lireJournal(sujet: 'livreur' | 'commercant', id: string): Promise<EvenementJournal[]> {
  const { data, error } = await supabase
    .from('onboarding_events')
    .select('id,de,vers,motif,at,auteur:profiles!par(full_name)')
    .eq('sujet', sujet)
    .eq('sujet_id', id)
    .order('at', { ascending: false })
    .limit(50)
  lever(error)
  return ((data as unknown as LigneJournal[] | null) ?? []).map((e) => ({
    id: e.id,
    de: e.de,
    vers: e.vers,
    motif: e.motif,
    auteur: e.auteur?.full_name ?? null,
    date: e.at,
  }))
}

export async function chargerDossierLivreur(driverId: string): Promise<DossierLivreur> {
  const [docs, itv, trn, qz, onb, sig, journal, ident] = await Promise.all([
    supabase
      .from('driver_documents')
      .select('id,doc_type,file_url,is_verified,rejected_reason,uploaded_at,issued_at,expires_at')
      .eq('driver_id', driverId)
      .order('uploaded_at', { ascending: false }),
    supabase
      .from('driver_interviews')
      .select('id,canevas_version,score,elimine,recommandation,notes,conducted_at')
      .eq('driver_id', driverId)
      .order('conducted_at', { ascending: false }),
    supabase
      .from('driver_trainings')
      .select('id,session_date,formateur,notes')
      .eq('driver_id', driverId)
      .order('session_date', { ascending: false }),
    supabase
      .from('driver_quiz_attempts')
      .select('id,submitted_at,score_pct,bonnes,total,reussi')
      .eq('driver_id', driverId)
      .order('submitted_at', { ascending: false }),
    supabase.from('driver_onboarding').select('quiz_deblocage_at').eq('driver_id', driverId).maybeSingle(),
    supabase
      .from('contract_signatures')
      .select(COLONNES_SIGNATURE)
      .eq('signer_id', driverId)
      .eq('kind', 'livreur')
      .order('created_at', { ascending: false }),
    lireJournal('livreur', driverId),
    supabase.from('driver_identite').select('*').eq('driver_id', driverId).maybeSingle(),
  ])
  for (const r of [docs, itv, trn, qz, onb, sig, ident]) lever(r.error)

  return {
    identite: (ident.data as LigneIdentite | null) ?? null,
    pieces: ((docs.data as LignePiece[] | null) ?? []).map((p) => versPiece(p, LIBELLE_DOC_LIVREUR)),
    entretiens: (
      (itv.data as Array<{
        id: string
        canevas_version: string
        score: number | string
        elimine: boolean
        recommandation: Recommandation
        notes: string | null
        conducted_at: string
      }> | null) ?? []
    ).map((e): EntretienEnregistre => ({
      id: e.id,
      version: e.canevas_version,
      score: Number(e.score),
      elimine: e.elimine,
      recommandation: e.recommandation,
      notes: e.notes,
      date: e.conducted_at,
    })),
    formations: (
      (trn.data as Array<{ id: string; session_date: string; formateur: string; notes: string | null }> | null) ?? []
    ).map((f): FormationEnregistree => ({ id: f.id, date: f.session_date, formateur: f.formateur, notes: f.notes })),
    tentatives: (
      (qz.data as Array<{
        id: string
        submitted_at: string
        score_pct: number | string
        bonnes: number
        total: number
        reussi: boolean
      }> | null) ?? []
    ).map((t): TentativeQuiz => ({
      id: t.id,
      date: t.submitted_at,
      scorePct: Number(t.score_pct),
      bonnes: t.bonnes,
      total: t.total,
      reussi: t.reussi,
    })),
    quizDeblocageLe: (onb.data as { quiz_deblocage_at: string | null } | null)?.quiz_deblocage_at ?? null,
    signatures: ((sig.data as LigneSignature[] | null) ?? []).map(versSignature),
    journal,
  }
}

export async function chargerDossierMarchand(merchantId: string): Promise<DossierMarchand> {
  const [docs, cpt, sig, journal, ident] = await Promise.all([
    supabase
      .from('merchant_documents')
      .select('id,doc_type,file_url,is_verified,rejected_reason,uploaded_at')
      .eq('merchant_id', merchantId)
      .order('uploaded_at', { ascending: false }),
    supabase
      .from('merchant_payout_accounts')
      .select('provider,phone,holder_name,verifie,verifie_le')
      .eq('merchant_id', merchantId)
      .maybeSingle(),
    supabase
      .from('contract_signatures')
      .select(COLONNES_SIGNATURE)
      .eq('merchant_id', merchantId)
      .eq('kind', 'commercant')
      .order('created_at', { ascending: false }),
    lireJournal('commercant', merchantId),
    supabase.from('merchant_identite').select('*').eq('merchant_id', merchantId).maybeSingle(),
  ])
  for (const r of [docs, cpt, sig, ident]) lever(r.error)

  const c = cpt.data as { provider: string; phone: string; holder_name: string; verifie: boolean; verifie_le: string | null } | null
  const compte: CompteReversement | null = c
    ? { fournisseur: c.provider, telephone: c.phone, titulaire: c.holder_name, verifie: c.verifie, verifieLe: c.verifie_le }
    : null
  return {
    identite: (ident.data as LigneIdentite | null) ?? null,
    pieces: ((docs.data as LignePiece[] | null) ?? []).map((p) => versPiece(p, LIBELLE_DOC_MARCHAND)),
    compte,
    signatures: ((sig.data as LigneSignature[] | null) ?? []).map(versSignature),
    journal,
  }
}

// ── Pièces ───────────────────────────────────────────────────────────────

/** URL signée COURTE (5 min) d'un fichier du bucket privé `onboarding` — jamais d'URL publique. */
export async function urlSigneePiece(chemin: string): Promise<string> {
  const { data, error } = await supabase.storage.from('onboarding').createSignedUrl(chemin, 300)
  lever(error)
  if (!data?.signedUrl) throw new Error('Aperçu indisponible : le fichier est introuvable.')
  return data.signedUrl
}

type TablePiece = 'driver_documents' | 'merchant_documents'

export async function validerPiece(table: TablePiece, id: string): Promise<void> {
  const { data, error } = await supabase.from(table).update({ is_verified: true }).eq('id', id).select('id')
  lever(error)
  exigerLigne(data, 'Validation de la pièce')
}

export async function rejeterPiece(table: TablePiece, id: string, motif: string): Promise<void> {
  const m = motif.trim()
  if (!m) throw new Error(traduireErreurOnboarding('motif_rejet_requis'))
  const { data, error } = await supabase
    .from(table)
    .update({ rejected_reason: m, is_verified: false })
    .eq('id', id)
    .select('id')
  lever(error)
  exigerLigne(data, 'Rejet de la pièce')
}

// ── Livreur ──────────────────────────────────────────────────────────────

export async function definirPermisRequis(driverId: string, requis: boolean): Promise<void> {
  const { data, error } = await supabase
    .from('driver_onboarding')
    .update({ permis_requis: requis })
    .eq('driver_id', driverId)
    .select('driver_id')
  lever(error)
  exigerLigne(data, 'Mise à jour du permis')
}

/**
 * Change le statut d'un livreur : un simple UPDATE, la base valide la
 * transition et ses préconditions (`refuse` et `suspendu` exigent un motif).
 */
export async function changerStatutLivreur(driverId: string, statut: string, motif?: string): Promise<void> {
  const { data, error } = await supabase
    .from('driver_onboarding')
    .update({ status: statut, ...(motif?.trim() ? { motif: motif.trim() } : {}) })
    .eq('driver_id', driverId)
    .select('driver_id')
  lever(error)
  exigerLigne(data, 'Changement de statut')
}

export interface EntretienAEnregistrer {
  driverId: string
  canevasVersion: string
  reponses: Record<string, unknown>
  score: number
  elimine: boolean
  recommandation: Recommandation
  notes: string
}

/**
 * Enregistre un entretien. `interviewer_id` n'est PAS envoyé : la base le pose
 * (défaut `auth.uid()`) et la policy exige qu'il soit égal à l'appelant.
 */
export async function enregistrerEntretien(e: EntretienAEnregistrer): Promise<void> {
  const { error } = await supabase.from('driver_interviews').insert({
    driver_id: e.driverId,
    canevas_version: e.canevasVersion,
    reponses: e.reponses,
    score: e.score,
    elimine: e.elimine,
    recommandation: e.recommandation,
    notes: e.notes.trim() || null,
  })
  lever(error)
}

export async function enregistrerFormation(
  driverId: string,
  date: string,
  formateur: string,
  notes: string,
): Promise<void> {
  if (!formateur.trim()) throw new Error('Indiquez le nom du formateur.')
  const { error } = await supabase.from('driver_trainings').insert({
    driver_id: driverId,
    session_date: date,
    formateur: formateur.trim(),
    notes: notes.trim() || null,
  })
  lever(error)
}

export async function debloquerQuiz(driverId: string): Promise<void> {
  const { error } = await supabase.rpc('debloquer_quiz', { p_driver: driverId })
  lever(error)
}

// ── Commerçant ───────────────────────────────────────────────────────────

export async function changerStatutMarchand(merchantId: string, statut: string, motif?: string): Promise<void> {
  const { data, error } = await supabase
    .from('merchants')
    .update({ onboarding_status: statut, ...(motif?.trim() ? { onboarding_motif: motif.trim() } : {}) })
    .eq('id', merchantId)
    .select('id')
  lever(error)
  exigerLigne(data, 'Changement de statut')
}

export async function verifierCompteReversement(merchantId: string): Promise<void> {
  const { data, error } = await supabase
    .from('merchant_payout_accounts')
    .update({ verifie: true })
    .eq('merchant_id', merchantId)
    .select('merchant_id')
  lever(error)
  exigerLigne(data, 'Vérification du compte')
}

// ── Contrats ─────────────────────────────────────────────────────────────

export interface EnvoiContrat {
  signerId: string
  kind: 'livreur' | 'commercant'
  merchantId?: string
  envoyerEmail: boolean
}

/** Edge function `envoyer-contrat-signature` (staff) : crée le document Documenso et renvoie le lien. */
export async function envoyerContratSignature(e: EnvoiContrat): Promise<ReponseEnvoiContrat> {
  return appelerFonction<ReponseEnvoiContrat>('envoyer-contrat-signature', {
    signer_id: e.signerId,
    kind: e.kind,
    ...(e.merchantId ? { merchant_id: e.merchantId } : {}),
    envoyer_email: e.envoyerEmail,
  })
}

/** Réponse des RPC `enregistrer_identite_*` : la ligne rangée et ce qu'il en manque encore (règle de la base). */
export interface ResultatIdentite {
  identite: LigneIdentite
  manquants: string[]
}

/** Enregistre l'identité juridique d'un marchand (RPC : valide, normalise, écrit). */
export async function enregistrerIdentiteMarchand(merchantId: string, saisie: IdentiteSaisie): Promise<ResultatIdentite> {
  const { data, error } = await supabase.rpc('enregistrer_identite_marchand', {
    p_merchant: merchantId,
    p_identite: versPayload('commercant', saisie),
  })
  lever(error)
  return data as ResultatIdentite
}

/** Idem pour un livreur (le staff complète la fiche d'un candidat). */
export async function enregistrerIdentiteLivreur(driverId: string, saisie: IdentiteSaisie): Promise<ResultatIdentite> {
  const { data, error } = await supabase.rpc('enregistrer_identite_livreur', {
    p_identite: versPayload('livreur', saisie),
    p_driver: driverId,
  })
  lever(error)
  return data as ResultatIdentite
}

/**
 * Aperçu du contrat RENDU pour un partenaire, tel qu'il sera signé (edge function `contrat-rendu`, mode staff :
 * rien n'est rangé). Échoue avec la liste de ce qui manque si l'identité est incomplète.
 */
export async function apercuContratRendu(
  sujet: SujetContrat,
  cible: { merchantId?: string; signerId?: string },
): Promise<ReponseContratRendu> {
  return appelerFonction<ReponseContratRendu>('contrat-rendu', {
    kind: sujet,
    ...(cible.merchantId ? { merchant_id: cible.merchantId } : {}),
    ...(cible.signerId ? { signer_id: cible.signerId } : {}),
  })
}

interface LigneModele {
  id: string
  kind: 'livreur' | 'commercant'
  version: number
  titre: string
  corps_md: string
  exclusivite_mois: number | null
  provisoire: boolean
  published_at: string | null
}

const COLONNES_MODELE = 'id,kind,version,titre,corps_md,exclusivite_mois,provisoire,published_at'

const versModele = (m: LigneModele): ModeleContrat => ({
  id: m.id,
  kind: m.kind,
  version: m.version,
  titre: m.titre,
  corps: m.corps_md,
  exclusiviteMois: m.exclusivite_mois,
  provisoire: m.provisoire,
  publieLe: m.published_at,
})

export async function listerModelesContrat(): Promise<ModeleContrat[]> {
  const { data, error } = await supabase
    .from('contract_templates')
    .select(COLONNES_MODELE)
    .order('kind')
    .order('version', { ascending: false })
  lever(error)
  return ((data as LigneModele[] | null) ?? []).map(versModele)
}

export interface ChampsModele {
  titre: string
  corps: string
  /** Obligatoire pour `commercant` à la publication ; doit rester null pour `livreur`. */
  exclusiviteMois: number | null
  provisoire: boolean
}

const colonnesModele = (c: ChampsModele, kind: 'livreur' | 'commercant') => ({
  titre: c.titre.trim(),
  corps_md: c.corps,
  exclusivite_mois: kind === 'commercant' ? c.exclusiviteMois : null,
  provisoire: c.provisoire,
})

/** Nouveau brouillon : version = max + 1 du `kind` (lu au moment de la création). */
export async function creerBrouillonModele(kind: 'livreur' | 'commercant', c: ChampsModele): Promise<ModeleContrat> {
  const { data: dernier, error: e1 } = await supabase
    .from('contract_templates')
    .select('version')
    .eq('kind', kind)
    .order('version', { ascending: false })
    .limit(1)
    .maybeSingle()
  lever(e1)
  const version = ((dernier as { version: number } | null)?.version ?? 0) + 1
  const { data, error } = await supabase
    .from('contract_templates')
    .insert({ kind, version, ...colonnesModele(c, kind) })
    .select(COLONNES_MODELE)
    .single()
  lever(error)
  return versModele(data as LigneModele)
}

export async function enregistrerBrouillonModele(
  id: string,
  kind: 'livreur' | 'commercant',
  c: ChampsModele,
): Promise<void> {
  const { data, error } = await supabase
    .from('contract_templates')
    .update(colonnesModele(c, kind))
    .eq('id', id)
    .select('id')
  lever(error)
  exigerLigne(data, 'Enregistrement du brouillon')
}

/**
 * Publie un brouillon (avec ses dernières modifications, en un seul UPDATE).
 * La base refuse un texte `provisoire` et pose elle-même l'horloge ; une fois
 * publiée, la version est immuable.
 */
export async function publierModele(id: string, kind: 'livreur' | 'commercant', c: ChampsModele): Promise<void> {
  const { data, error } = await supabase
    .from('contract_templates')
    .update({ ...colonnesModele(c, kind), published_at: new Date().toISOString() })
    .eq('id', id)
    .select('id')
  lever(error)
  exigerLigne(data, 'Publication du contrat')
}
